import {
  format,
  isAfter,
  isBefore,
  parse,
  startOfToday,
  subDays,
} from 'date-fns';
import { enUS } from 'date-fns/locale';

type BuslineSchedulesDataDaysOfWeekItem =
  | 'mon'
  | 'tue'
  | 'wed'
  | 'thu'
  | 'fri'
  | 'sat'
  | 'sun';

interface BuslineScheduleDto {
  daysOfWeek: BuslineSchedulesDataDaysOfWeekItem[];
  id: number;
  startAt: string;
  endAt: string;
}

export const abbreviateDay = (day: BuslineSchedulesDataDaysOfWeekItem) => {
  const dayAbbreviations: Record<BuslineSchedulesDataDaysOfWeekItem, string> = {
    mon: 'Seg',
    tue: 'Ter',
    wed: 'Qua',
    thu: 'Qui',
    fri: 'Sex',
    sat: 'Sáb',
    sun: 'Dom',
  };

  return dayAbbreviations[day] || day;
};

export const isScheduleActive = (
  schedule: Pick<BuslineScheduleDto, 'daysOfWeek' | 'startAt' | 'endAt'>,
  marginMilliseconds: number = 0,
  now: Date = new Date(),
) => {
  const today = format(now, 'E', {
    locale: enUS,
  }).toLowerCase() as BuslineSchedulesDataDaysOfWeekItem;
  const yesterday = format(subDays(now, 1), 'E', {
    locale: enUS,
  }).toLowerCase() as BuslineSchedulesDataDaysOfWeekItem;

  const isToday = schedule.daysOfWeek.includes(today);
  const isYesterday = schedule.daysOfWeek.includes(yesterday);

  const startAt = parse(schedule.startAt, 'HH:mm:ss', startOfToday());
  startAt.setMilliseconds(startAt.getMilliseconds() - marginMilliseconds);
  const endAt = parse(schedule.endAt, 'HH:mm:ss', startOfToday());

  if (isAfter(startAt, endAt)) {
    // Overnight window: active if still in the previous day's tail or today's head.
    const isAfterStartAt = !isBefore(now, startAt);
    const isBeforeEndAt = isBefore(now, endAt);

    return (isToday && isAfterStartAt) || (isYesterday && isBeforeEndAt);
  }

  const isBetween = !isBefore(now, startAt) && isBefore(now, endAt);

  const isActive = isToday && isBetween;

  return isActive;
};
